import React, { useState, useEffect } from 'react';
import { MapPin, ShoppingCart, ChevronLeft, Star, Clock, Shield, Award, CheckCircle, Search, Info, Book, Send, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../components/CartContext';
import { Helmet } from 'react-helmet';
import PurchaseOptionSelector from './PurchaseOptionSelector';
import ProductDetailsEnhanced from './ProductDetailsEnhanced';
import { keyframes } from '@mui/material/styles';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogContentText,
    DialogActions,
    Button,
    Container,
    Grid,
    Paper,
    Typography,
    Alert,
    IconButton,
    Box,
    Chip,
    TextField,
    InputAdornment,
    CircularProgress,
    Tooltip,
    Zoom,
    Divider,
    Card,
    CardContent,
    CardMedia,
    Badge,
    useMediaQuery,
    useTheme,
    Fade,
} from '@mui/material';
import { History } from 'lucide-react';

// Animation keyframes
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const float = keyframes`
    0%, 100% { 
        transform: translateY(0) rotate(0deg); 
    }
    50% { 
        transform: translateY(-20px) rotate(5deg); 
    }
`;

const pulse = keyframes`
    0%, 100% { 
        transform: scale(1);
        opacity: 1;
    }
    50% { 
        transform: scale(1.05);
        opacity: 0.9;
    }
`;

const Shop = () => {
    const [selectedState, setSelectedState] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cartDialogOpen, setCartDialogOpen] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const [showInfoPanel, setShowInfoPanel] = useState(false);
    const [pageTitle, setPageTitle] = useState('Contractor Exam Prep Materials | Pass Your Test');
    const [pageDescription, setPageDescription] = useState(
        'Get comprehensive contractor exam prep materials for Louisiana, Mississippi, South Carolina, Tennessee, and NASCLA. Pass your contractor licensing test with our specialized study resources.',
    );
    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false); // Track URL parameter processing
    const [purchaseOptionDialogOpen, setPurchaseOptionDialogOpen] = useState(false);
    const [selectedProductForOptions, setSelectedProductForOptions] = useState(null);
    const navigate = useNavigate();
    const { addToCart, cart } = useCart();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const STATES = ['Louisiana', 'Mississippi', 'South Carolina', 'Tennessee', 'NASCLA'];
    const STATE_COLORS = {
        Louisiana: '#fc6117',
        Mississippi: '#fc6117',
        'South Carolina': '#fc6117',
        Tennessee: '#fc6117',
        NASCLA: '#fc6117',
    };

    // SVG paths for state outlines
    const STATE_ICONS = {
        Louisiana: (color) => (
            <svg width="32" height="32" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M8.5,5.5l3.5,0.25l7-0.25l1,0.75l1.25,2l4,0.25l5.25-1.75l3.25,0.5l0.75,2l-1.25,1.75l1,2.5
                l3.5,2.5l0.5,2.75l3.75,2.25l-1.5,2.5l-0.25,2.25l-1,1.5l-5.25,0.5l1.25,2.25l-2.5,1.75c0,0,0,2-0.25,2.5
                s-4.75,1.5-4.75,1.5l-1.75,3.25l-2.75,0.25l0.25-3.75l-2.5-4l-3.25-1.5v-2.5l1.25-1.5l-2.75-3.25l-6-2l-2-3l0.75-5.5
                L8.5,13.5l2.25-3.25L8.5,5.5z"
                    fill="none"
                    stroke={color}
                    strokeWidth="1.2"
                />
            </svg>
        ),
        Mississippi: (color) => (
            <svg width="32" height="32" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M22,6l1.75,1.5L24,11l-1.25,1.25L20.5,14l-0.75,4.5l-2.25,1.25L16,22l-4.25,1L11,26.5l-2.5,1
                l-1,3l1.75,2.75l0.5,4.25H14l0.5-2.5l2.75-2l0.25-3l2.25-2.25L19.5,25l2,1.5l2-0.75l1.25-3.5l1-1.75L27,20l2.25-1l2.5-0.25
                L31,14l-0.25-2.5L29,9.75L28.75,6l-3.5-0.75L24,2.75L22,6z"
                    fill="none"
                    stroke={color}
                    strokeWidth="1.2"
                />
            </svg>
        ),
        'South Carolina': (color) => (
            <svg width="32" height="32" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M25,6.5l3.25,0.5l1.75,2l2.75,6.75l4.75,3.75l-1.5,1.75l-1.75,4l-3,1.5l-1.75,4L27,34.5l-4.5,1
                l-3.25-0.5l-5.75-6l-3-1l-1.75-5l2-6.75l0.75-3l2.5-1.75L14.5,7l2.5-1l3-1.75L25,6.5z"
                    fill="none"
                    stroke={color}
                    strokeWidth="1.2"
                />
            </svg>
        ),
        Tennessee: (color) => (
            <svg width="32" height="32" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M5.5,15l31,1l0.5,8l-2.25,1.25L33.5,28l-3.75,0.5l-1.5-0.25L27,30.5l-5.25-1L20,28.25
                l-3.25,1.5L13.5,28l-5.25-1.5l-3,0.75V15z"
                    fill="none"
                    stroke={color}
                    strokeWidth="1.2"
                />
            </svg>
        ),
        NASCLA: (color) => <Zap size={32} color={color} />,
    };

    const STATE_DESCRIPTIONS = {
        Louisiana: 'Comprehensive exam prep for Louisiana contractor licensing',
        Mississippi: 'Specialized materials for Mississippi contractor exams',
        'South Carolina': 'Targeted study resources for South Carolina contractors',
        Tennessee: 'Complete preparation for Tennessee contractor licensing',
        NASCLA: 'National contractor exam preparation resources',
    };

    // Scroll event handler
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollToTop(window.scrollY > 400);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        // Skip initial fetch if URL parameters haven't been processed yet
        if (!urlParamsProcessed) {
            console.log('Skipping initial product fetch until URL parameters are processed');
            return;
        }

        console.log('Fetching products with state:', selectedState, 'and type:', selectedType);

        const fetchProducts = async () => {
            setLoading(true);
            try {
                let url = `${process.env.REACT_APP_API_URL}/api/products/filter?`;

                if (selectedType === 'Previously Used Exam Questions') {
                    url += `type=${selectedType}`;
                } else if (selectedState) {
                    // Ensure that the state parameter sent to API matches exactly what's in the database
                    // We use the properly cased version from STATES array rather than the URL parameter
                    const stateParam = STATES.find((s) => s.toLowerCase() === selectedState.toLowerCase()) || selectedState;
                    url += `state=${encodeURIComponent(stateParam)}&type=Original Practice Test`;
                } else {
                    // Fetch all products when no state or type is selected
                    url = `${process.env.REACT_APP_API_URL}/api/products`;
                }

                // Debug
                console.log('Fetching products from URL:', url);

                const response = await fetch(url, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                if (!response.ok) throw new Error('Failed to fetch products');

                const data = await response.json();
                console.log('Retrieved products data:', data);

                const productsWithImages = data.data.data.map((product) => ({
                    ...product,
                    imageUrl: getImageUrl(product.imageUrl),
                }));

                // Client-side filtering for better precision
                let finalProducts = productsWithImages;
                if (selectedState) {
                    console.log(`Applying client-side filter for state: ${selectedState}`);

                    // Normalize the selectedState for case-insensitive comparison
                    const normalizedSelectedState = selectedState.toLowerCase();

                    finalProducts = productsWithImages.filter((product) => {
                        // Make sure we're dealing with an array of states
                        const productStates = Array.isArray(product.state) ? product.state : [product.state];

                        // Case-insensitive comparison
                        const hasMatchingState = productStates.some((state) => {
                            if (!state) return false;
                            return state.toLowerCase() === normalizedSelectedState;
                        });

                        console.log(`Product ${product.name} states: ${productStates.join(', ')} - Match: ${hasMatchingState}`);
                        return hasMatchingState;
                    });
                    console.log(`Found ${finalProducts.length} products after client filtering`);

                    // Removed fallback that shows all products when no NASCLA products are found
                    // When no products are found for a state, we should show "no products" instead of all products
                    if (finalProducts.length === 0) {
                        console.log(`No products found for state: ${selectedState}. Keeping empty results.`);
                        // Keep finalProducts as empty array - the UI will show "Coming Soon" or similar message
                    }
                }

                setProducts(finalProducts);
                setFilteredProducts(finalProducts);
            } catch (err) {
                setError('Failed to load products');
                console.error('Error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [selectedState, selectedType, urlParamsProcessed]);

    // Filter products based on search query
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredProducts(products);
            return;
        }

        const filtered = products.filter(
            (product) =>
                product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                product.description?.toLowerCase().includes(searchQuery.toLowerCase()),
        );

        setFilteredProducts(filtered);
    }, [searchQuery, products]);

    // Update SEO metadata based on selections
    useEffect(() => {
        if (selectedState) {
            setPageTitle(`${selectedState} Contractor Exam Prep Materials | Pass Your Test`);
            setPageDescription(
                `Comprehensive ${selectedState} contractor exam preparation materials. Study resources designed to help you pass your ${selectedState} contractor licensing test with confidence.`,
            );
        } else if (selectedType === 'Previously Used Exam Questions') {
            setPageTitle('Previously Used Contractor Exam Questions | Practice with Real Tests');
            setPageDescription(
                'Practice with authentic questions from past contractor exams. Prepare effectively for your licensing test with our collection of previously used exam questions.',
            );
        } else {
            setPageTitle('Contractor Exam Prep Materials | Pass Your Test');
            setPageDescription(
                'Get comprehensive contractor exam prep materials for Louisiana, Mississippi, South Carolina, Tennessee, and NASCLA. Pass your contractor licensing test with our specialized study resources.',
            );
        }
    }, [selectedState, selectedType]);

    const getImageUrl = (path) => {
        if (!path) return '/api/placeholder/400/300';

        // If it's already a full URL, return it as is
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }

        // Clean the path by removing leading slashes
        const cleanPath = path.replace(/^\/+/, '');

        // Return the full URL to the image
        return `${process.env.REACT_APP_API_URL}/${cleanPath}`;
    };

    const handleAddToCart = (product, event) => {
        if (event) {
            event.stopPropagation();
        }
        // If product has purchase options, show product details instead
        if (product.purchaseOptions && product.purchaseOptions.length > 1) {
            setCurrentProduct(product);
            setShowInfoPanel(true);
        } else {
            // For products without options, use the old flow
            setSelectedProductForOptions(product);
            setPurchaseOptionDialogOpen(true);
        }
    };

    const handlePurchaseOptionSelect = (optionType, option) => {
        const productWithOption = {
            ...selectedProductForOptions,
            selectedOption: optionType,
            price: option.price,
            displayName: option.displayName,
            isTaxable: option.isTaxable,
        };
        setCurrentProduct(productWithOption);
        addToCart(productWithOption);
        setCartDialogOpen(true);
        setPurchaseOptionDialogOpen(false);
    };

    const handleProductClick = (product) => {
        // Show product detail view or modal
        setCurrentProduct(product);
        setShowInfoPanel(true);
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const CartDialog = () => (
        <Dialog open={cartDialogOpen} onClose={() => setCartDialogOpen(false)} maxWidth="sm" fullWidth TransitionComponent={Zoom}>
            <DialogTitle
                sx={{
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'text.primary',
                    pt: 3,
                }}
            >
                Added to Cart!
            </DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 2,
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: 'success.light',
                            borderRadius: '50%',
                            p: 1,
                            mb: 2,
                        }}
                    >
                        <CheckCircle sx={{ color: 'success.main', fontSize: 32 }} />
                    </Box>
                    <DialogContentText sx={{ textAlign: 'center' }}>{currentProduct?.name} has been added to your cart.</DialogContentText>

                    {currentProduct && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mt: 2,
                                p: 2,
                                bgcolor: 'background.paper',
                                borderRadius: 2,
                                boxShadow: 1,
                                width: '100%',
                            }}
                        >
                            <img
                                src={currentProduct.imageUrl}
                                alt={`${currentProduct.name} thumbnail`}
                                style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, marginRight: 16 }}
                            />
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {currentProduct.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    ${currentProduct.price?.toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ mt: 3, width: '100%' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Cart Summary:
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Items in cart:</Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {cart.length}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Total:</Typography>
                            <Typography variant="body2" fontWeight="bold">
                                ${cart.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    justifyContent: 'center',
                    pb: 3,
                    gap: 2,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={() => setCartDialogOpen(false)}
                    sx={{
                        borderColor: 'grey.300',
                        color: 'text.primary',
                        '&:hover': {
                            borderColor: 'grey.400',
                            bgcolor: 'grey.50',
                        },
                    }}
                >
                    Continue Shopping
                </Button>
                <Button
                    variant="contained"
                    onClick={() => navigate('/cart')}
                    startIcon={<ShoppingCart />}
                    sx={{
                        bgcolor: '#ff5e1c',
                        '&:hover': {
                            bgcolor: '#e54e0f',
                        },
                    }}
                >
                    View Cart
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ProductInfoPanel is now replaced by ProductDetails component

    const StateCard = ({ state }) => {
        const stateColor = STATE_COLORS[state] || '#ff5e1c';
        const stateDescription = STATE_DESCRIPTIONS[state] || `Access exam prep materials specific to ${state}`;

        return (
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 5, sm: 6, md: 7 },
                    borderRadius: '28px',
                    cursor: 'pointer',
                    position: 'relative',
                    background: 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'visible',
                    isolation: 'isolate',
                    boxShadow: `
                        0 1px 2px rgba(0, 0, 0, 0.02),
                        0 4px 8px rgba(0, 0, 0, 0.04),
                        inset 0 1px 0 rgba(255, 255, 255, 0.9)
                    `,
                    // Subtle gradient overlay
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: '28px',
                        background: `
                            linear-gradient(135deg, rgba(252, 97, 23, 0.03) 0%, transparent 50%, rgba(255, 140, 75, 0.02) 100%),
                            repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 0, 0, 0.01) 2px, rgba(0, 0, 0, 0.01) 4px)
                        `,
                        opacity: 0,
                        transition: 'opacity 0.6s ease',
                        pointerEvents: 'none',
                    },
                    // Premium gradient border
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: -1,
                        borderRadius: '28px',
                        padding: '1px',
                        background: 'linear-gradient(135deg, rgba(252, 97, 23, 0.4), rgba(255, 140, 75, 0.2), rgba(252, 97, 23, 0.4))',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        opacity: 0,
                        transition: 'opacity 0.6s ease',
                        backgroundSize: '200% 200%',
                        animation: `${keyframes`
                            0% { background-position: 0% 50%; }
                            50% { background-position: 100% 50%; }
                            100% { background-position: 0% 50%; }
                        `} 4s ease infinite`,
                    },
                    '&:hover': {
                        transform: 'translateY(-16px)',
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid rgba(252, 97, 23, 0.15)',
                        boxShadow: `
                            0 4px 8px rgba(252, 97, 23, 0.04),
                            0 12px 24px rgba(252, 97, 23, 0.08),
                            0 24px 48px rgba(252, 97, 23, 0.12),
                            0 32px 64px rgba(0, 0, 0, 0.08),
                            inset 0 1px 0 rgba(255, 255, 255, 1)
                        `,
                        '&::before': {
                            opacity: 1,
                        },
                        '&::after': {
                            opacity: 1,
                        },
                        '& .state-badge': {
                            opacity: 1,
                            transform: 'translateY(0)',
                        },
                        '& .state-icon-container': {
                            transform: 'translateY(-8px) scale(1.08)',
                            background: 'linear-gradient(135deg, #fc6117 0%, #ff8c4b 100%)',
                            boxShadow: `
                                0 16px 32px rgba(252, 97, 23, 0.25),
                                0 8px 16px rgba(252, 97, 23, 0.15),
                                0 0 0 1px rgba(252, 97, 23, 0.1),
                                inset 0 2px 4px rgba(255, 255, 255, 0.3),
                                inset 0 -2px 4px rgba(0, 0, 0, 0.1)
                            `,
                        },
                        '& .state-icon-inner': {
                            transform: 'rotate(-6deg) scale(1.1)',
                            '& svg': {
                                color: 'white !important',
                                filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))',
                            },
                            '& svg path': {
                                stroke: 'white !important',
                            },
                        },
                        '& .state-name': {
                            background: 'linear-gradient(135deg, #fc6117 0%, #ff8c4b 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            letterSpacing: '-0.03em',
                        },
                        '& .state-description': {
                            color: '#1A202C',
                        },
                        '& .state-divider': {
                            width: '80px',
                            opacity: 1,
                        },
                        '& .hover-glow': {
                            opacity: 1,
                            transform: 'scale(1.5)',
                        },
                    },
                }}
                onClick={() => {
                    setSelectedState(state);
                    const url = new URL(window.location);
                    url.searchParams.set('state', state);
                    window.history.pushState({}, '', url);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
            >
                {/* Premium Badge */}
                <Box
                    className="state-badge"
                    sx={{
                        position: 'absolute',
                        top: -12,
                        right: 20,
                        px: 3,
                        py: 1,
                        borderRadius: '20px',
                        background: 'linear-gradient(135deg, #fc6117 0%, #ff8c4b 100%)',
                        boxShadow: '0 4px 12px rgba(252, 97, 23, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                        opacity: 0,
                        transform: 'translateY(-8px)',
                        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 10,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 800,
                            color: 'white',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                        }}
                    >
                        Available
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, pt: 2 }}>
                    {/* Icon with sophisticated container */}
                    <Box
                        className="state-icon-container"
                        sx={{
                            position: 'relative',
                            width: 140,
                            height: 140,
                            mb: 4,
                            borderRadius: '32px',
                            background: 'linear-gradient(145deg, #FFF5F0 0%, #FFE8DC 100%)',
                            border: '1px solid rgba(252, 97, 23, 0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: `
                                0 10px 20px rgba(252, 97, 23, 0.08),
                                0 4px 8px rgba(252, 97, 23, 0.05),
                                inset 0 2px 0 rgba(255, 255, 255, 0.8),
                                inset 0 -2px 4px rgba(252, 97, 23, 0.05)
                            `,
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '30px',
                                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 100%)',
                            },
                        }}
                    >
                        {/* Hover glow effect */}
                        <Box
                            className="hover-glow"
                            sx={{
                                position: 'absolute',
                                inset: -30,
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(252, 97, 23, 0.25) 0%, transparent 70%)',
                                opacity: 0,
                                transition: 'all 0.6s ease',
                                filter: 'blur(20px)',
                                zIndex: -1,
                            }}
                        />
                        <Box
                            className="state-icon-inner"
                            sx={{
                                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '& svg': {
                                    width: 56,
                                    height: 56,
                                    transition: 'all 0.5s ease',
                                },
                            }}
                        >
                            {STATE_ICONS[state]('#fc6117')}
                        </Box>
                    </Box>

                    <Typography
                        className="state-name"
                        variant="h3"
                        sx={{
                            mb: 2.5,
                            fontWeight: 900,
                            fontSize: { xs: '1.5rem', sm: '1.75rem' },
                            color: '#0F172A',
                            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                            letterSpacing: '-0.02em',
                            textAlign: 'center',
                        }}
                    >
                        {state}
                    </Typography>

                    <Box
                        className="state-divider"
                        sx={{
                            width: 60,
                            height: 4,
                            borderRadius: 4,
                            background: 'linear-gradient(90deg, transparent 0%, #fc6117 50%, transparent 100%)',
                            mb: 3,
                            opacity: 0.6,
                            transition: 'all 0.5s ease',
                        }}
                    />

                    <Typography
                        className="state-description"
                        variant="body1"
                        sx={{
                            color: '#64748B',
                            textAlign: 'center',
                            lineHeight: 1.8,
                            fontSize: '1.0625rem',
                            transition: 'color 0.5s ease',
                            fontWeight: 400,
                            maxWidth: 320,
                        }}
                    >
                        {stateDescription}
                    </Typography>
                </Box>
            </Paper>
        );
    };

    const PrevExamCard = () => (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 5, sm: 6, md: 7 },
                borderRadius: '28px',
                cursor: 'pointer',
                position: 'relative',
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                overflow: 'visible',
                isolation: 'isolate',
                boxShadow: `
                    0 1px 2px rgba(0, 0, 0, 0.02),
                    0 4px 8px rgba(0, 0, 0, 0.04),
                    inset 0 1px 0 rgba(255, 255, 255, 0.9)
                `,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '28px',
                    background: `
                        linear-gradient(135deg, rgba(147, 51, 234, 0.03) 0%, transparent 50%, rgba(168, 85, 247, 0.02) 100%),
                        repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 0, 0, 0.01) 2px, rgba(0, 0, 0, 0.01) 4px)
                    `,
                    opacity: 0,
                    transition: 'opacity 0.6s ease',
                    pointerEvents: 'none',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: -1,
                    borderRadius: '28px',
                    padding: '1px',
                    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.4), rgba(168, 85, 247, 0.2), rgba(147, 51, 234, 0.4))',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    opacity: 0,
                    transition: 'opacity 0.6s ease',
                    backgroundSize: '200% 200%',
                    animation: `${keyframes`
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                    `} 4s ease infinite`,
                },
                '&:hover': {
                    transform: 'translateY(-16px)',
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid rgba(147, 51, 234, 0.15)',
                    boxShadow: `
                        0 4px 8px rgba(147, 51, 234, 0.04),
                        0 12px 24px rgba(147, 51, 234, 0.08),
                        0 24px 48px rgba(147, 51, 234, 0.12),
                        0 32px 64px rgba(0, 0, 0, 0.08),
                        inset 0 1px 0 rgba(255, 255, 255, 1)
                    `,
                    '&::before': {
                        opacity: 1,
                    },
                    '&::after': {
                        opacity: 1,
                    },
                    '& .prev-exam-icon-container': {
                        transform: 'translateY(-8px) scale(1.08)',
                        background: 'linear-gradient(135deg, #9333ea 0%, #a855f7 100%)',
                        boxShadow: `
                            0 16px 32px rgba(147, 51, 234, 0.25),
                            0 8px 16px rgba(147, 51, 234, 0.15),
                            0 0 0 1px rgba(147, 51, 234, 0.1),
                            inset 0 2px 4px rgba(255, 255, 255, 0.3),
                            inset 0 -2px 4px rgba(0, 0, 0, 0.1)
                        `,
                    },
                    '& .prev-exam-icon-inner': {
                        transform: 'rotate(6deg) scale(1.1)',
                        '& svg': {
                            color: 'white !important',
                            filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))',
                        },
                    },
                    '& .prev-exam-title': {
                        background: 'linear-gradient(135deg, #9333ea 0%, #a855f7 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        letterSpacing: '-0.03em',
                    },
                    '& .prev-exam-description': {
                        color: '#1A202C',
                    },
                    '& .hover-glow-purple': {
                        opacity: 1,
                        transform: 'scale(1.5)',
                    },
                },
            }}
            onClick={() => {
                setSelectedType('Previously Used Exam Questions');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1,
                    maxWidth: 900,
                    mx: 'auto',
                    pt: 2,
                }}
            >
                <Grid container spacing={4} alignItems="center">
                    {/* Icon Side */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box
                                className="prev-exam-icon-container"
                                sx={{
                                    position: 'relative',
                                    width: 140,
                                    height: 140,
                                    borderRadius: '32px',
                                    background: 'linear-gradient(145deg, #FAF5FF 0%, #F3E8FF 100%)',
                                    border: '1px solid rgba(147, 51, 234, 0.12)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: `
                                        0 10px 20px rgba(147, 51, 234, 0.08),
                                        0 4px 8px rgba(147, 51, 234, 0.05),
                                        inset 0 2px 0 rgba(255, 255, 255, 0.8),
                                        inset 0 -2px 4px rgba(147, 51, 234, 0.05)
                                    `,
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: 0,
                                        borderRadius: '30px',
                                        background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 100%)',
                                    },
                                }}
                            >
                                <Box
                                    className="hover-glow-purple"
                                    sx={{
                                        position: 'absolute',
                                        inset: -30,
                                        borderRadius: '50%',
                                        background: 'radial-gradient(circle, rgba(147, 51, 234, 0.25) 0%, transparent 70%)',
                                        opacity: 0,
                                        transition: 'all 0.6s ease',
                                        filter: 'blur(20px)',
                                        zIndex: -1,
                                    }}
                                />
                                <Box
                                    className="prev-exam-icon-inner"
                                    sx={{
                                        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <History style={{ color: '#9333ea', width: 56, height: 56 }} />
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Content Side */}
                    <Grid item xs={12} md={8}>
                        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                            <Typography
                                className="prev-exam-title"
                                variant="h3"
                                sx={{
                                    mb: 2.5,
                                    fontWeight: 900,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                                    color: '#0F172A',
                                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Previously Used Exam Questions
                            </Typography>

                            <Box
                                sx={{
                                    width: 60,
                                    height: 4,
                                    borderRadius: 4,
                                    background: 'linear-gradient(90deg, #9333ea 0%, #a855f7 100%)',
                                    mb: 3,
                                    mx: { xs: 'auto', md: 0 },
                                }}
                            />

                            <Typography
                                className="prev-exam-description"
                                variant="body1"
                                sx={{
                                    color: '#64748B',
                                    lineHeight: 1.8,
                                    fontSize: '1.0625rem',
                                    transition: 'color 0.5s ease',
                                    fontWeight: 400,
                                    mb: 3,
                                }}
                            >
                                Practice with authentic questions from past contractor exams to enhance your preparation
                            </Typography>

                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    px: 2.5,
                                    py: 1.25,
                                    borderRadius: 3,
                                    background: 'rgba(147, 51, 234, 0.08)',
                                    border: '1.5px solid rgba(147, 51, 234, 0.15)',
                                    boxShadow: '0 2px 4px rgba(147, 51, 234, 0.05)',
                                }}
                            >
                                <Info size={18} style={{ color: '#9333ea' }} />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#6B21A8',
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    Not Eligible for No Pass No Pay Guarantee
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );

    const ProductCard = ({ product }) => {
        // Find primary state color for styling
        const primaryState = product.state && product.state[0];
        const stateColor = STATE_COLORS[primaryState] || '#ff5e1c';

        return (
            <Card
                elevation={0}
                sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: '#F0F2F5',
                    background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%)',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, ${stateColor} 0%, ${stateColor}cc 100%)`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 2,
                    },
                    '&:hover': {
                        transform: 'translateY(-12px)',
                        boxShadow: `0 24px 48px -16px ${stateColor}40, 0 12px 24px -12px ${stateColor}30`,
                        borderColor: `${stateColor}50`,
                        background: '#FFFFFF',
                        '&::before': {
                            transform: 'scaleX(1)',
                        },
                        '& .product-image': {
                            transform: 'scale(1.08)',
                        },
                        '& .product-overlay': {
                            opacity: 1,
                        },
                        '& .product-price': {
                            transform: 'scale(1.05)',
                        },
                    },
                }}
                onClick={() => handleProductClick(product)}
            >
                <Box
                    sx={{
                        position: 'relative',
                        height: 220,
                        overflow: 'hidden',
                        background: 'linear-gradient(180deg, #F8F9FA 0%, #E9ECEF 100%)',
                    }}
                >
                    <img
                        src={product.imageUrl}
                        alt={`${product.name} - ${product.state ? product.state.join(', ') : ''} Contractor Exam Prep Material`}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                        className="product-image"
                        loading="lazy"
                    />
                    <Box
                        className="product-overlay"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            opacity: 0,
                            transition: 'opacity 0.4s ease',
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={(e) => handleAddToCart(product, e)}
                            startIcon={<Info />}
                            sx={{
                                bgcolor: 'white',
                                color: stateColor,
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    bgcolor: 'white',
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                },
                                px: 3,
                                py: 1.25,
                            }}
                        >
                            View Details
                        </Button>
                    </Box>
                </Box>
                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                        variant="h6"
                        sx={{ mb: 1.5, fontWeight: 700, fontSize: '1.125rem', color: '#1A202C', letterSpacing: '-0.01em' }}
                    >
                        {product.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        {[...Array(product.rating || 5)].map((_, i) => (
                            <Star
                                key={i}
                                style={{
                                    width: 16,
                                    height: 16,
                                    color: '#FBBF24',
                                    fill: '#FBBF24',
                                }}
                            />
                        ))}
                        <Typography variant="body2" sx={{ ml: 1, color: '#718096', fontWeight: 500, fontSize: '0.875rem' }}>
                            ({product.reviews || '800+'})
                        </Typography>
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            flexGrow: 1,
                            color: '#4A5568',
                            lineHeight: 1.6,
                            fontSize: '0.9375rem',
                        }}
                    >
                        {product.description ||
                            'Comprehensive exam preparation materials designed to help contractors pass their licensing exams.'}
                    </Typography>

                    <Box
                        sx={{
                            mb: 2,
                            mt: 'auto',
                            p: 1.5,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.05) 100%)',
                            border: '1.5px solid rgba(76, 175, 80, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Clock style={{ color: '#4CAF50', flexShrink: 0 }} size={18} />
                        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.4, color: '#2D3748', fontSize: '0.8125rem' }}>
                            Includes 6-month practice exam subscription
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            pt: 2.5,
                            borderTop: '2px solid',
                            borderColor: '#F0F2F5',
                            mt: 0,
                        }}
                    >
                        <Typography
                            className="product-price"
                            variant="h5"
                            sx={{
                                background: `linear-gradient(135deg, ${stateColor} 0%, ${stateColor}cc 100%)`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                fontWeight: 800,
                                fontSize: '1.75rem',
                                transition: 'transform 0.3s ease',
                            }}
                        >
                            ${product.price?.toFixed(2)}
                        </Typography>
                        <Tooltip title="View details" arrow>
                            <IconButton
                                size="small"
                                sx={{
                                    color: stateColor,
                                    bgcolor: `${stateColor}10`,
                                    '&:hover': {
                                        bgcolor: `${stateColor}20`,
                                    },
                                }}
                            >
                                <Info size={20} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    // Removed renderProductHeader in favor of inline semantic HTML with proper h1/h2 tags

    const renderLoadingSpinner = (color) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Box
                sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    border: '4px solid',
                    borderColor: 'grey.200',
                    borderTopColor: color,
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                        '0%': {
                            transform: 'rotate(0deg)',
                        },
                        '100%': {
                            transform: 'rotate(360deg)',
                        },
                    },
                }}
            />
        </Box>
    );

    // Structure for schema.org data
    const getSchemaOrgData = () => {
        if (!selectedState && !selectedType) {
            return {
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                itemListElement: STATES.map((state, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@type': 'Product',
                        name: `${state} Contractor Exam Prep Materials`,
                        description: STATE_DESCRIPTIONS[state],
                        url: `${window.location.origin}/shop?state=${state}`,
                    },
                })),
            };
        } else if (filteredProducts.length > 0) {
            return {
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                itemListElement: filteredProducts.map((product, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@type': 'Product',
                        name: product.name,
                        description: product.description || 'Contractor exam preparation material',
                        offers: {
                            '@type': 'Offer',
                            price: product.price,
                            priceCurrency: 'USD',
                        },
                    },
                })),
            };
        }
        return null;
    };

    // Render main selection page vs product listings
    // Instead of conditional return, we'll use conditional rendering of the content
    const renderMainContent = () => {
        if (!selectedState && !selectedType) {
            return (
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    {/* HERO SECTION - Enhanced */}
                    <Container maxWidth="lg">
                        <Box
                            sx={{
                                textAlign: 'center',
                                pt: { xs: 8, md: 12 },
                                pb: { xs: 6, md: 8 },
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '-30%',
                                    left: '-10%',
                                    width: '120%',
                                    height: '160%',
                                    background: 'radial-gradient(ellipse at center, rgba(252, 97, 23, 0.04) 0%, transparent 60%)',
                                    pointerEvents: 'none',
                                    animation: `${float} 20s ease-in-out infinite`,
                                },
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: '-20%',
                                    right: '-5%',
                                    width: '80%',
                                    height: '100%',
                                    background: 'radial-gradient(ellipse at center, rgba(147, 51, 234, 0.03) 0%, transparent 60%)',
                                    pointerEvents: 'none',
                                    animation: `${float} 25s ease-in-out infinite reverse`,
                                },
                            }}
                        >
                            <Fade in={true} timeout={800}>
                                <Box sx={{ mb: 4, display: 'inline-flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 2,
                                            background: 'linear-gradient(90deg, transparent 0%, #fc6117 100%)',
                                            borderRadius: 1,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            background: 'linear-gradient(135deg, #fc6117 0%, #ff8c4b 100%)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            fontWeight: 700,
                                            fontSize: '0.9375rem',
                                            letterSpacing: '0.15em',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        Professional Exam Preparation
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 2,
                                            background: 'linear-gradient(90deg, #fc6117 0%, transparent 100%)',
                                            borderRadius: 1,
                                        }}
                                    />
                                </Box>
                            </Fade>
                            <Typography
                                variant="h1"
                                sx={{
                                    mb: 4,
                                    fontWeight: 900,
                                    fontSize: { xs: '2.75rem', sm: '3.5rem', md: '5rem' },
                                    background: 'linear-gradient(135deg, #1A202C 0%, #2D3748 50%, #4A5568 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    letterSpacing: '-0.04em',
                                    lineHeight: 1.1,
                                    animation: `${fadeInUp} 0.8s ease-out`,
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                            >
                                Pass Your Contractor Exam
                                <br />
                                <Box
                                    component="span"
                                    sx={{
                                        background: 'linear-gradient(135deg, #fc6117 0%, #ff8c4b 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    }}
                                >
                                    With Confidence
                                </Box>
                            </Typography>
                            <Box
                                sx={{
                                    width: 120,
                                    height: 5,
                                    mx: 'auto',
                                    mb: 5,
                                    borderRadius: 3,
                                    background: 'linear-gradient(90deg, #fc6117 0%, #ff8c4b 100%)',
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                            />
                            <Typography
                                variant="h2"
                                sx={{
                                    maxWidth: 800,
                                    mx: 'auto',
                                    mb: 6,
                                    fontSize: { xs: '1.125rem', md: '1.5rem' },
                                    fontWeight: 400,
                                    color: '#4A5568',
                                    lineHeight: 1.7,
                                    animation: `${fadeInUp} 0.9s ease-out`,
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                            >
                                Comprehensive study materials designed by industry experts to help you pass your contractor licensing exam
                                on the first try
                            </Typography>

                            {/* Stats Section */}
                            <Grid container spacing={4} sx={{ maxWidth: 900, mx: 'auto', mb: 4, position: 'relative', zIndex: 1 }}>
                                {[
                                    { value: '30,000+', label: 'Students Passed' },
                                    { value: '99%', label: 'Pass Rate' },
                                    { value: '24/7', label: 'Access' },
                                    { value: '100%', label: 'Money-Back' },
                                ].map((stat, index) => (
                                    <Grid item xs={6} sm={3} key={index}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                textAlign: 'center',
                                                background: 'rgba(255, 255, 255, 0.8)',
                                                backdropFilter: 'blur(10px)',
                                                border: '2px solid',
                                                borderColor: 'rgba(252, 97, 23, 0.1)',
                                                borderRadius: 3,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    borderColor: 'rgba(252, 97, 23, 0.3)',
                                                    boxShadow: '0 12px 24px -8px rgba(252, 97, 23, 0.15)',
                                                },
                                            }}
                                        >
                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    fontSize: { xs: '2rem', md: '2.5rem' },
                                                    fontWeight: 900,
                                                    background: 'linear-gradient(135deg, #fc6117 0%, #ff8c4b 100%)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    backgroundClip: 'text',
                                                    mb: 0.5,
                                                }}
                                            >
                                                {stat.value}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#718096',
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                {stat.label}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Container>

                    {/* STATE SELECTION SECTION - Enhanced */}
                    <Box sx={{ background: 'linear-gradient(180deg, rgba(252, 97, 23, 0.02) 0%, transparent 100%)', py: 10 }}>
                        <Container maxWidth="lg">
                            <Box sx={{ mb: 8, textAlign: 'center' }}>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontSize: { xs: '2rem', md: '2.75rem' },
                                        fontWeight: 800,
                                        mb: 3,
                                        background: 'linear-gradient(135deg, #1A202C 0%, #2D3748 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    Choose Your State
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: '1.125rem',
                                        color: '#718096',
                                        maxWidth: 600,
                                        mx: 'auto',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    Select your state to access tailored exam preparation materials
                                </Typography>
                            </Box>

                            <Grid container spacing={3}>
                                {STATES.map((state, index) => (
                                    <Grid item xs={12} sm={6} lg={4} key={state}>
                                        <Fade in={true} timeout={800 + index * 100}>
                                            <Box>
                                                <StateCard state={state} />
                                            </Box>
                                        </Fade>
                                    </Grid>
                                ))}
                            </Grid>

                            <Box sx={{ mt: 4 }}>
                                <Fade in={true} timeout={1400}>
                                    <Box>
                                        <PrevExamCard />
                                    </Box>
                                </Fade>
                            </Box>
                        </Container>
                    </Box>

                    {/* HOW IT WORKS SECTION - New */}
                    <Box sx={{ py: 10, background: '#FFFFFF' }}>
                        <Container maxWidth="lg">
                            <Box sx={{ textAlign: 'center', mb: 8 }}>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontSize: { xs: '2rem', md: '2.75rem' },
                                        fontWeight: 800,
                                        mb: 3,
                                        background: 'linear-gradient(135deg, #1A202C 0%, #2D3748 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    How It Works
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: '1.125rem',
                                        color: '#718096',
                                        maxWidth: 600,
                                        mx: 'auto',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    Get certified in just 4 simple steps
                                </Typography>
                            </Box>

                            <Grid container spacing={4}>
                                {[
                                    {
                                        step: '01',
                                        title: 'Select Your State',
                                        description: 'Choose your state and get access to state-specific study materials',
                                        icon: <MapPin size={32} />,
                                        gradient: 'linear-gradient(135deg, #fc6117 0%, #ff8c4b 100%)',
                                    },
                                    {
                                        step: '02',
                                        title: 'Study the Materials',
                                        description: 'Access comprehensive study guides and practice exams 24/7',
                                        icon: <Book size={32} />,
                                        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    },
                                    {
                                        step: '03',
                                        title: 'Take Practice Tests',
                                        description: 'Test your knowledge with our realistic practice exams',
                                        icon: <CheckCircle size={32} />,
                                        gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                    },
                                    {
                                        step: '04',
                                        title: 'Pass Your Exam',
                                        description: 'Pass with confidence or get your money back',
                                        icon: <Award size={32} />,
                                        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                    },
                                ].map((item, index) => (
                                    <Grid item xs={12} sm={6} md={3} key={index}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 4,
                                                height: '100%',
                                                textAlign: 'center',
                                                position: 'relative',
                                                background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFBFC 100%)',
                                                border: '2px solid #E8EDF2',
                                                borderRadius: 3,
                                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                overflow: 'hidden',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: '4px',
                                                    background: item.gradient,
                                                    transform: 'scaleX(0)',
                                                    transformOrigin: 'left',
                                                    transition: 'transform 0.4s ease',
                                                },
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
                                                    borderColor: 'transparent',
                                                    '&::before': {
                                                        transform: 'scaleX(1)',
                                                    },
                                                    '& .step-number': {
                                                        background: item.gradient,
                                                        transform: 'scale(1.1)',
                                                        color: 'white',
                                                    },
                                                    '& .step-icon': {
                                                        transform: 'scale(1.1) rotate(5deg)',
                                                        background: item.gradient,
                                                    },
                                                    '& .step-icon svg': {
                                                        color: 'white',
                                                    },
                                                },
                                            }}
                                        >
                                            <Typography
                                                className="step-number"
                                                sx={{
                                                    display: 'inline-block',
                                                    fontSize: '0.875rem',
                                                    fontWeight: 800,
                                                    px: 2,
                                                    py: 0.5,
                                                    mb: 3,
                                                    borderRadius: 2,
                                                    background: 'rgba(252, 97, 23, 0.1)',
                                                    color: '#fc6117',
                                                    transition: 'all 0.3s ease',
                                                    letterSpacing: '0.05em',
                                                }}
                                            >
                                                STEP {item.step}
                                            </Typography>
                                            <Box
                                                className="step-icon"
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    mx: 'auto',
                                                    mb: 3,
                                                    borderRadius: 3,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: 'rgba(252, 97, 23, 0.08)',
                                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    '& svg': {
                                                        color: '#fc6117',
                                                        transition: 'color 0.3s ease',
                                                    },
                                                }}
                                            >
                                                {item.icon}
                                            </Box>
                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    fontSize: '1.125rem',
                                                    fontWeight: 700,
                                                    mb: 1.5,
                                                    color: '#1A202C',
                                                    letterSpacing: '-0.01em',
                                                }}
                                            >
                                                {item.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#718096',
                                                    lineHeight: 1.6,
                                                    fontSize: '0.9375rem',
                                                }}
                                            >
                                                {item.description}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Container>
                    </Box>

                    {/* WHY CHOOSE US SECTION */}
                    <Box sx={{ py: 10, background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%)' }}>
                        <Container maxWidth="lg">
                            <Box sx={{ textAlign: 'center', mb: 8 }}>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontSize: { xs: '2rem', md: '2.75rem' },
                                        fontWeight: 800,
                                        mb: 3,
                                        background: 'linear-gradient(135deg, #1A202C 0%, #2D3748 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    Why Choose Our Study Materials
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: '1.125rem',
                                        color: '#718096',
                                        maxWidth: 600,
                                        mx: 'auto',
                                        mb: 2,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    Everything you need to pass your contractor exam with confidence
                                </Typography>
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 4,
                                        mx: 'auto',
                                        borderRadius: 2,
                                        background: 'linear-gradient(90deg, #fc6117 0%, #ff8c4b 100%)',
                                    }}
                                />
                            </Box>

                            <Grid container spacing={3} sx={{ mt: 2 }}>
                                {[
                                    {
                                        icon: <Shield style={{ width: 36, height: 36 }} />,
                                        title: 'No Pass No Pay Guarantee',
                                        desc: "Money back if you don't pass your exam*",
                                        link: '/guarantee',
                                        gradient: 'linear-gradient(135deg, #fc6117 0%, #ff8c4b 100%)',
                                    },
                                    {
                                        icon: <Clock style={{ width: 36, height: 36 }} />,
                                        title: '24/7 Access',
                                        desc: 'Study when and where it works for you',
                                        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    },
                                    {
                                        icon: <Award style={{ width: 36, height: 36 }} />,
                                        title: 'Expert Content',
                                        desc: 'Created by industry professionals',
                                        gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                    },
                                    {
                                        icon: <Book style={{ width: 36, height: 36 }} />,
                                        title: 'Up-to-Date',
                                        desc: 'Latest code requirements',
                                        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                    },
                                ].map((benefit, index) => (
                                    <Grid item xs={12} sm={6} md={3} key={index}>
                                        <Paper
                                            elevation={0}
                                            onClick={benefit.link ? () => navigate(benefit.link) : undefined}
                                            sx={{
                                                p: 4,
                                                height: '100%',
                                                textAlign: 'center',
                                                borderRadius: 3,
                                                background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFBFC 100%)',
                                                cursor: benefit.link ? 'pointer' : 'default',
                                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                border: '2px solid',
                                                borderColor: '#E8EDF2',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: benefit.gradient,
                                                    opacity: 0,
                                                    transition: 'opacity 0.4s ease',
                                                    zIndex: 0,
                                                },
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
                                                    borderColor: 'transparent',
                                                    background: '#FFFFFF',
                                                    '&::before': {
                                                        opacity: 0.05,
                                                    },
                                                    '& .benefit-icon-wrapper': {
                                                        background: benefit.gradient,
                                                        transform: 'scale(1.1) rotate(5deg)',
                                                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                                                    },
                                                    '& .benefit-icon-wrapper svg': {
                                                        color: 'white !important',
                                                    },
                                                    '& .benefit-title': {
                                                        background: benefit.gradient,
                                                        WebkitBackgroundClip: 'text',
                                                        WebkitTextFillColor: 'transparent',
                                                        backgroundClip: 'text',
                                                    },
                                                },
                                            }}
                                        >
                                            <Box
                                                className="benefit-icon-wrapper"
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    mx: 'auto',
                                                    mb: 3,
                                                    borderRadius: 3,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: `${benefit.gradient.split('(')[0]}(135deg, rgba(252, 97, 23, 0.08) 0%, rgba(255, 140, 75, 0.05) 100%)`,
                                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    border: '1px solid rgba(0, 0, 0, 0.04)',
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    '& svg': {
                                                        color: '#fc6117',
                                                        transition: 'color 0.3s ease',
                                                    },
                                                }}
                                            >
                                                {benefit.icon}
                                            </Box>
                                            <Typography
                                                className="benefit-title"
                                                variant="h3"
                                                sx={{
                                                    mb: 1.5,
                                                    fontWeight: 700,
                                                    fontSize: '1.0625rem',
                                                    color: '#1A202C',
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    transition: 'all 0.3s ease',
                                                    letterSpacing: '-0.01em',
                                                }}
                                            >
                                                {benefit.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#718096',
                                                    fontSize: '0.9375rem',
                                                    lineHeight: 1.6,
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    fontWeight: 400,
                                                }}
                                            >
                                                {benefit.desc}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Container>
                    </Box>

                    {/* GUARANTEE CTA SECTION - New */}
                    <Box
                        sx={{
                            py: 10,
                            background: 'linear-gradient(135deg, rgba(252, 97, 23, 0.03) 0%, rgba(255, 140, 75, 0.02) 100%)',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <Container maxWidth="md">
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 4, md: 6 },
                                    borderRadius: 4,
                                    background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFBFC 100%)',
                                    border: '2px solid',
                                    borderColor: 'rgba(252, 97, 23, 0.2)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    textAlign: 'center',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '6px',
                                        background: 'linear-gradient(90deg, #fc6117 0%, #ff8c4b 100%)',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        mx: 'auto',
                                        mb: 3,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'linear-gradient(135deg, rgba(252, 97, 23, 0.1) 0%, rgba(255, 140, 75, 0.08) 100%)',
                                        border: '3px solid rgba(252, 97, 23, 0.2)',
                                    }}
                                >
                                    <Shield style={{ width: 48, height: 48, color: '#fc6117' }} />
                                </Box>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontSize: { xs: '2rem', md: '2.5rem' },
                                        fontWeight: 800,
                                        mb: 2,
                                        background: 'linear-gradient(135deg, #1A202C 0%, #2D3748 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    No Pass, No Pay Guarantee
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: '1.125rem',
                                        color: '#4A5568',
                                        mb: 4,
                                        lineHeight: 1.7,
                                        maxWidth: 600,
                                        mx: 'auto',
                                    }}
                                >
                                    We're so confident in our materials that if you don't pass your exam, we'll refund your purchase. Your
                                    success is our commitment.
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/guarantee')}
                                    sx={{
                                        background: 'linear-gradient(135deg, #fc6117 0%, #ff8c4b 100%)',
                                        color: 'white',
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1.0625rem',
                                        fontWeight: 700,
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        boxShadow: '0 8px 20px rgba(252, 97, 23, 0.25)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 12px 28px rgba(252, 97, 23, 0.35)',
                                            background: 'linear-gradient(135deg, #e54e0f 0%, #fc6117 100%)',
                                        },
                                    }}
                                >
                                    Learn More About Our Guarantee
                                </Button>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        mt: 3,
                                        color: '#A0AEC0',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    * See guarantee terms and conditions for details
                                </Typography>
                            </Paper>
                        </Container>
                    </Box>
                </Box>
            );
        } else {
            return (
                <Container maxWidth="lg">
                    <Button
                        variant="outlined"
                        startIcon={<ChevronLeft />}
                        onClick={() => {
                            setSelectedType(null);
                            setSelectedState(null);
                            setSearchQuery('');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        sx={{
                            mb: 4,
                            borderColor: headerColor,
                            color: headerColor,
                            borderRadius: 2,
                            px: 3,
                            '&:hover': {
                                borderColor: headerColor,
                                bgcolor: `${headerColor}10`,
                            },
                        }}
                    >
                        Back to Selection
                    </Button>

                    {/* Product listing header with semantic HTML for SEO */}
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            {headerIcon}
                            <Typography variant="h1" sx={{ fontWeight: 'bold', fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
                                {headerTitle}
                            </Typography>
                        </Box>
                        <Typography
                            variant="h2"
                            sx={{ color: 'text.secondary', fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 'normal' }}
                        >
                            {headerSubtitle}
                        </Typography>
                    </Box>

                    {/* Search and filter bar */}
                    <Paper
                        elevation={2}
                        sx={{
                            p: 2,
                            mb: 4,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <TextField
                            fullWidth
                            placeholder="Search products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            variant="outlined"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" size={20} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                                            <Typography variant="caption">Clear</Typography>
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2 },
                            }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                            <Badge badgeContent={cart.length} color="error" sx={{ mr: 1 }}>
                                <IconButton color="primary" onClick={() => navigate('/cart')}>
                                    <ShoppingCart size={24} />
                                </IconButton>
                            </Badge>
                        </Box>
                    </Paper>

                    {loading ? (
                        renderLoadingSpinner(headerColor)
                    ) : error ? (
                        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    ) : filteredProducts.length === 0 ? (
                        <Paper
                            sx={{
                                textAlign: 'center',
                                py: 8,
                                px: 4,
                                borderRadius: 4,
                                bgcolor: '#f9fafb',
                            }}
                        >
                            {searchQuery ? (
                                <>
                                    <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 'bold', mb: 2 }}>
                                        No products found
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        We couldn't find any products matching "{searchQuery}"
                                    </Typography>
                                    <Button variant="outlined" onClick={() => setSearchQuery('')} sx={{ mt: 3 }}>
                                        Clear Search
                                    </Button>
                                </>
                            ) : (
                                <Typography variant="h4" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                                    Coming Soon
                                </Typography>
                            )}
                        </Paper>
                    ) : (
                        <>
                            {searchQuery && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body1">
                                        Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'result' : 'results'}
                                        {searchQuery ? ` for "${searchQuery}"` : ''}
                                    </Typography>
                                </Box>
                            )}
                            <Grid container spacing={4}>
                                {filteredProducts.map((product) => (
                                    <Grid item xs={12} sm={6} lg={4} key={product._id}>
                                        <ProductCard product={product} />
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}

                    {/* Testimonials section */}
                    {!loading && filteredProducts.length > 0 && (
                        <Box sx={{ mt: 8 }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    textAlign: 'center',
                                    fontSize: { xs: '1.75rem', md: '2.25rem' },
                                    fontWeight: 'bold',
                                    mb: 5,
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: -10,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 60,
                                        height: 3,
                                        bgcolor: headerColor,
                                        borderRadius: 3,
                                    },
                                }}
                            >
                                What Our Customers Say
                            </Typography>

                            <Grid container spacing={4}>
                                {[
                                    {
                                        quote: "I passed my contractor's exam on the first try! The practice tests were exactly what I needed to prepare.",
                                        name: 'Michael T.',
                                        title: 'General Contractor',
                                        rating: 5,
                                    },
                                    {
                                        quote: 'The study materials were comprehensive and well organized. Worth every penny!',
                                        name: 'Sarah L.',
                                        title: 'Electrical Contractor',
                                        rating: 5,
                                    },
                                    {
                                        quote: 'These practice tests covered exactly what was on the actual exam. Highly recommended!',
                                        name: 'Robert J.',
                                        title: 'Plumbing Contractor',
                                        rating: 5,
                                    },
                                ].map((testimonial, index) => (
                                    <Grid item xs={12} md={4} key={index}>
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: 4,
                                                borderRadius: 4,
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                border: '1px solid #f0f0f0',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: 4,
                                                    height: '100%',
                                                    bgcolor: headerColor,
                                                },
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', mb: 2 }}>
                                                {[...Array(testimonial.rating)].map((_, i) => (
                                                    <Star
                                                        key={i}
                                                        style={{
                                                            width: 16,
                                                            height: 16,
                                                            color: '#fbbf24',
                                                            fill: '#fbbf24',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    mb: 3,
                                                    fontStyle: 'italic',
                                                    flexGrow: 1,
                                                    position: 'relative',
                                                    '&::before': {
                                                        content: '"\\201C"', // Unicode for left double quotation mark
                                                        fontSize: '2.5rem',
                                                        color: 'rgba(0,0,0,0.1)',
                                                        position: 'absolute',
                                                        left: -10,
                                                        top: -10,
                                                        fontFamily: 'Georgia, serif',
                                                    },
                                                }}
                                            >
                                                {testimonial.quote}
                                            </Typography>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {testimonial.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {testimonial.title}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </Container>
            );
        }
    };

    // We're now duplicating this logic for simplicity - to avoid hooks order issues
    const isStateView = !!selectedState;
    const headerColor = isStateView ? STATE_COLORS[selectedState] || '#ff5e1c' : '#9333ea';
    const headerIcon = isStateView ? (
        <MapPin style={{ color: headerColor, width: 32, height: 32 }} />
    ) : (
        <History style={{ color: headerColor, width: 32, height: 32 }} />
    );
    const headerTitle = isStateView ? `${selectedState} Contractor Exam Prep` : 'Previously Used Exam Questions';
    const headerSubtitle = isStateView
        ? `Comprehensive study materials tailored for ${selectedState} contractors`
        : 'Practice with authentic questions from past exams';

    // Update SEO meta data based on selections - this effect must be placed here, not conditionally
    // Handle URL parameters for direct navigation
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const stateParam = params.get('state');
        const typeParam = params.get('type');

        console.log('URL parameters:', { stateParam, typeParam });

        if (stateParam) {
            // Find matching state regardless of case
            const matchedState = STATES.find((state) => state.toLowerCase() === stateParam.toLowerCase());

            if (matchedState) {
                console.log(`Setting selected state to: ${matchedState}`);
                setSelectedState(matchedState);

                // Ensure the URL reflects the correct case
                if (stateParam !== matchedState) {
                    const url = new URL(window.location);
                    url.searchParams.set('state', matchedState);
                    window.history.replaceState({}, '', url);
                }
            } else {
                console.log(`State parameter not recognized: ${stateParam}`);
            }
        } else if (typeParam === 'previously-used') {
            setSelectedType('Previously Used Exam Questions');
        }

        // Mark URL parameters as processed
        setUrlParamsProcessed(true);
    }, []);

    useEffect(() => {
        // Update page title and description based on selection
        if (selectedState) {
            setPageTitle(`${selectedState} Contractor Exam Prep Materials | Pass Your Test`);
            setPageDescription(
                `Comprehensive ${selectedState} contractor exam preparation materials. Study resources designed to help you pass your ${selectedState} contractor licensing test with confidence.`,
            );
        } else if (selectedType === 'Previously Used Exam Questions') {
            setPageTitle('Previously Used Contractor Exam Questions | Practice with Real Tests');
            setPageDescription(
                'Practice with authentic questions from past contractor exams. Prepare effectively for your licensing test with our collection of previously used exam questions.',
            );
        } else {
            setPageTitle('Contractor Exam Prep Materials | Pass Your Test');
            setPageDescription(
                'Get comprehensive contractor exam prep materials for Louisiana, Mississippi, South Carolina, Tennessee, and NASCLA. Pass your contractor licensing test with our specialized study resources.',
            );
        }
    }, [selectedState, selectedType]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 40%, #FFFFFF 100%)',
                py: 0,
                px: 0,
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '400px',
                    background: 'radial-gradient(ellipse at top, rgba(252, 97, 23, 0.03) 0%, transparent 60%)',
                    pointerEvents: 'none',
                    zIndex: 0,
                },
            }}
        >
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <meta
                    name="keywords"
                    content={
                        selectedState
                            ? `${selectedState} contractor exam, contractor license test, ${selectedState} contractor prep, contractor exam study material, pass ${selectedState} contractor test`
                            : selectedType === 'Previously Used Exam Questions'
                              ? 'previously used contractor exam questions, real contractor exam questions, practice contractor tests, authentic exam questions, contractor test prep'
                              : 'contractor exam prep, contractor license test, NASCLA contractor exam, state contractor license, contractor study materials'
                    }
                />
                <link
                    rel="canonical"
                    href={
                        selectedState
                            ? `${window.location.origin}/shop?state=${selectedState}`
                            : selectedType === 'Previously Used Exam Questions'
                              ? `${window.location.origin}/shop?type=previously-used`
                              : `${window.location.origin}/shop`
                    }
                />
                <meta name="robots" content="index, follow" />
                <script type="application/ld+json">{JSON.stringify(getSchemaOrgData())}</script>
            </Helmet>

            {renderMainContent()}

            {/* Scroll to top button */}
            <Zoom in={showScrollToTop}>
                <Box
                    onClick={scrollToTop}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        zIndex: 2,
                        bgcolor: '#fc6117',
                        color: 'white',
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        boxShadow: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'translateY(-5px)',
                        },
                    }}
                >
                    <ChevronLeft style={{ transform: 'rotate(90deg)' }} />
                </Box>
            </Zoom>

            <CartDialog />

            <ProductDetailsEnhanced
                open={showInfoPanel}
                onClose={() => {
                    setShowInfoPanel(false);
                    setCurrentProduct(null);
                }}
                product={currentProduct}
            />

            <PurchaseOptionSelector
                open={purchaseOptionDialogOpen}
                onClose={() => {
                    setPurchaseOptionDialogOpen(false);
                    setSelectedProductForOptions(null);
                }}
                product={selectedProductForOptions}
                onSelect={handlePurchaseOptionSelect}
                selectedOption="studyProgramOnly"
            />
        </Box>
    );
};

export default Shop;
